import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';

/**
 * The four decisions, in the order they should be offered.
 *
 * Cool-down first because it is the ordinary answer, and putting the dramatic
 * option last is part of what keeps it rare. Each carries the sentence a person
 * would actually say, not a severity label — the point of this feature is that
 * triage produces a decision rather than a number.
 */
export const DECISIONS = [
  {
    value: 'cooldown',
    label: 'Fix in cool-down',
    blurb: 'Small enough for the gap between cycles.',
    color: 'primary',
    Icon: BuildCircleOutlinedIcon,
  },
  {
    value: 'shape',
    label: 'Shape it',
    blurb: 'Too big for cool-down, so it has to win a bet.',
    color: 'secondary',
    Icon: ArchitectureIcon,
  },
  {
    value: 'emergency',
    label: 'Emergency',
    blurb: 'Breaks the cycle now, and costs a named bet.',
    color: 'error',
    Icon: LocalFireDepartmentIcon,
  },
  {
    value: 'wont_fix',
    label: 'Not doing',
    blurb: 'Decided against, out loud, so it stops resurfacing.',
    color: 'default',
    Icon: DoNotDisturbOnOutlinedIcon,
  },
];

export const DECISION_BY_VALUE = DECISIONS.reduce((acc, decision) => {
  acc[decision.value] = decision;
  return acc;
}, {});

/**
 * The buckets, in the order they are worth looking at. Untriaged leads because it
 * is the only one asking the team for something; everything else is a state of
 * work already decided on.
 */
export const BUCKETS = [
  { value: 'untriaged', label: 'Untriaged', hint: 'Waiting on a decision — the only list that needs you.' },
  { value: 'cooldown', label: 'Cool-down', hint: 'Queued for the gap between cycles.' },
  { value: 'shaping', label: 'Shaping', hint: 'Became a pitch and is competing for a bet.' },
  { value: 'emergency', label: 'Emergencies', hint: 'Interrupted a bet, and charged against it.' },
  { value: 'fading', label: 'Fading', hint: 'Nobody picked these up. Sweep them; real ones come back.' },
  { value: 'fixed', label: 'Fixed', hint: 'Done, and credited to the cycle that absorbed it.' },
  { value: 'archived', label: 'Archived', hint: 'Swept or decided against. Kept, not deleted.' },
];

export const BUCKET_BY_VALUE = BUCKETS.reduce((acc, bucket) => {
  acc[bucket.value] = bucket;
  return acc;
}, {});

/**
 * How a bug's age should read. Plain sentences rather than a date, because the
 * question being asked of this screen is "how long has this been sitting".
 */
export const describeAge = (bug) => {
  const days = bug.ageDays || 0;
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return `${Math.round(days / 30)} months ago`;
};
